import { useState } from "react";
import { useNavigate } from "react-router-dom-v5-compat";
import { useFeatureFlag } from "@apac/feature-flags";
import { removeSession } from "@b2bportal/core-auth";
import { IconNameEnum, useModuleBEM } from "@b2bportal/core-themes";
import type { PlatformComponentProps } from "@b2bportal/core-types";
import { Drawer, DrawerContent, DrawerTrigger, Icon } from "@b2bportal/core-ui";
import { useI18nContext } from "@hopper-b2b/i18n";
import { ExperimentVariant } from "@hopper-b2b/types";
import { NavigationItem } from "@hsbc/portal";
import {
  getFaqLink,
  HOME_PATH,
  CUSTOMER_SUPPORT_PATH,
  TRIPS_PATH,
  WALLET_PATH,
  FeatureFlagNames,
  INVALID_SESSION_PATH,
} from "@hsbc/utilities";
import clsx from "clsx";
import { v4 as uuidv4 } from "uuid";
import styles from "./styles.module.scss";

const version = import.meta.env.VITE_REACT_APP_VERSION ?? `local-${uuidv4()}`;

export const MobileNavigation = ({
  className,
  children,
}: PlatformComponentProps) => {
  const {
    t,
    language,
    brand: { urls },
  } = useI18nContext();
  const [block, cn] = useModuleBEM(styles, "NavigationContent");
  const enablePriceWatch =
    useFeatureFlag(
      FeatureFlagNames.ENABLE_PRICE_WATCH,
      ExperimentVariant.Available,
      true
    ) ?? true;

  const [openDrawer, setDrawer] = useState(false);
  const navigate = useNavigate();

  const isExternalUrl = (url: string) => url.startsWith("http");

  const navigationItems: Array<{
    title: string;
    link: string;
    isExternalLink?: boolean;
    icon?: IconNameEnum;
    onClick?: (link: string) => void;
  }> = [
    { title: t("goHome"), link: HOME_PATH, icon: IconNameEnum.search },
    { title: t("wallet.title"), link: WALLET_PATH, icon: IconNameEnum.wallet },
    { title: t("myTrips"), link: TRIPS_PATH, icon: IconNameEnum.trips },
    ...(enablePriceWatch
      ? [
          {
            title: t("watchedTrips"),
            link: "/watches",
            icon: IconNameEnum.watchedTrips,
          },
        ]
      : []),
    {
      title: t("faq"),
      link: getFaqLink(language),
      isExternalLink: true,
      icon: IconNameEnum.faq,
    },
    {
      title: t("customerSupport"),
      link: CUSTOMER_SUPPORT_PATH,
      icon: IconNameEnum.customerSupport,
    },
    {
      title: t("termsOfService"),
      link: urls.termsAndConditions,
      isExternalLink: isExternalUrl(urls.termsAndConditions),
    },
    {
      title: t("privacyPolicy"),
      link: urls.privacyPolicy,
      isExternalLink: isExternalUrl(urls.privacyPolicy),
    },
    {
      title: t("signOut"),
      link: INVALID_SESSION_PATH,
      onClick: (link: string) => {
        removeSession()
          .catch(console.error)
          .finally(() => {
            navigate(link);
            setDrawer(false);
          });
      },
    },
  ];

  return (
    <div className={clsx(block, className)}>
      <Drawer open={openDrawer} onClose={() => setDrawer(false)}>
        <DrawerTrigger
          onClick={() => setDrawer(true)}
          role="button"
          aria-expanded={openDrawer}
          aria-controls="mobile-navigation-drawer"
          aria-label={t("navigationMenu")}
        >
          <Icon iconName={IconNameEnum.drawer} aria-hidden="true" />
        </DrawerTrigger>
        <DrawerContent
          position="left"
          id="mobile-navigation-drawer"
          title={t("navigationMenu")}
          titleIsVisuallyHidden
        >
          <nav>
            <ul className={cn("list-container")}>
              {navigationItems.map((item) => (
                <NavigationItem
                  key={item.link}
                  {...item}
                  onClick={(link: string) => {
                    if (item.onClick != null) {
                      item.onClick(link);
                    } else if (item.isExternalLink ?? false) {
                      window.open(link, "_blank");
                    } else {
                      navigate(link);
                      setDrawer(false);
                    }
                  }}
                />
              ))}
            </ul>
          </nav>
          {children}
          <span className={cn("version")}>{version}</span>
        </DrawerContent>
      </Drawer>
    </div>
  );
};
